import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { baseURL } from "../../../../network/baseUrl";
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setLanguages } from "../../../../redux/slices/languageSlice";

const OtpVerification = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get user data from navigation state
  const { userId, email } = location.state || {};
  
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const inputRefs = useRef([]);

  const API_BASE_URL = `${baseURL}/auth/verify-otp/`;
  const RESEND_API_URL = `${baseURL}/auth/resend-otp/`;

  useEffect(() => {
    // Redirect to register if no user data
    if (!userId || !email) {
      navigate('/register');
      return;
    }

    const currentLang = localStorage.getItem("selectedLanguage") || "en";
    dispatch(setLanguages(currentLang));
    
    const disableBackForward = () => {
      window.history.pushState(null, '', window.location.href);
      window.onpopstate = () => {
        window.history.pushState(null, '', window.location.href);
      };
    };
    disableBackForward();
    i18n.changeLanguage(currentLang).then(() => setReady(true));

    // Focus on first input
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }

    return () => {
      window.onpopstate = null;
    };
  }, [userId, email, navigate, dispatch, i18n]);

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    const newOtp = [...otp];
    
    for (let i = 0; i < pastedData.length; i++) {
      newOtp[i] = pastedData[i];
    }
    
    setOtp(newOtp);
    
    // Focus on the next empty field or last field
    const nextIndex = Math.min(pastedData.length, 3);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const otpString = otp.join("");
    
    if (otpString.length !== 4) {
      setErrorMessage(t("otp.enterCompleteOtp") || "Please enter complete OTP");
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");
    setLoading(true);

    try {
      const response = await fetch(API_BASE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId,
          otp: parseInt(otpString),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSuccessMessage(t("otp.verificationSuccess") || "OTP verified successfully!");
        
        setTimeout(() => {
          // Navigate to login page after successful verification
          navigate('/login', { 
            state: { 
              message: "emailVerifiedSuccess",
              email: email 
            } 
          });
        }, 1500);
      } else {
        const error = await response.json();
        setErrorMessage(t("otp.invalidOtp") || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error:", error);
      setErrorMessage(t("otp.genericError") || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const response = await fetch(RESEND_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: userId, // Send user_id for resend
        }),
      });

      if (response.ok) {
        const data = await response.json();
      setSuccessMessage(t("otp.newOtpSent") || data.message || "A new OTP has been sent to your email.");
        setOtp(["", "", "", ""]); // Clear OTP fields
        inputRefs.current[0]?.focus(); // Focus on first input
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      } else {
        const error = await response.json();
        setErrorMessage(t("otp.resendError") || "Failed to resend OTP. Please try again.");
      }
    } catch (error) {
      console.error("Resend OTP Error:", error);
      setErrorMessage(t("otp.genericError") || "Something went wrong. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToRegister = () => {
    navigate('/register');
  };

  // Show loading or redirect if no user data
  if (!userId || !email) {
    return null;
  }

  return (
    <div className="min-h-screen w-full bg-gray-900 flex justify-center items-center p-2">
      <div className="w-full max-w-md bg-gray-800 relative">
        <button
          className="absolute top-2 right-2 text-white text-3xl bg-transparent border-0"
          onClick={() => navigate('/')}
        >
          &times;
        </button>

        <div className="p-3 md:p-5">
          <h2 className="text-center mb-3 text-white font-bold text-xl md:text-2xl">
            {t("otp.title") || "Verify OTP"}
          </h2>

          <p className="text-center mb-4 text-gray-400 text-sm">
            {t("otp.subtitle") || "Enter the 4-digit code sent to"} <br />
            <span className="text-white font-bold">{email}</span>
          </p>

          <form onSubmit={handleSubmit}>
            <div className="flex justify-center gap-2 mb-4">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  className="w-12 h-12 bg-gray-900 text-white border-2 border-gray-600 focus:border-blue-500 text-center text-xl font-bold rounded"
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  maxLength={1}
                  required
                />
              ))}
            </div>

            {errorMessage && (
              <div className="bg-red-600 text-white text-center p-1 mb-2 rounded text-sm" role="alert">
                {errorMessage}
              </div>
            )}

            {successMessage && (
              <div className="bg-green-600 text-white text-center p-1 mb-2 rounded text-sm" role="alert">
                {successMessage}
              </div>
            )}

            <button
              type="submit"
              className="w-full p-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-none transition-colors mb-3"
              disabled={loading || otp.join("").length !== 4}
            >
              {loading ? (
                <>
                  <span className="inline-block h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></span>
                  {t("otp.verifying") || "Verifying..."}
                </>
              ) : (
                t("otp.confirm") || "Confirm"
              )}
            </button>

            <button
              type="button"
              className="w-full p-2 bg-gray-600 hover:bg-gray-700 text-white font-bold rounded-none transition-colors mb-3"
              onClick={handleResendOtp}
              disabled={resendLoading || loading}
            >
              {resendLoading ? (
                <>
                  <span className="inline-block h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></span>
                  {t("otp.resending") || "Resending..."}
                </>
              ) : (
                t("otp.resendOtp") || "Resend OTP"
              )}
            </button>
          </form>

          <p className="mt-3 text-center text-white text-sm">
            <button
              className="text-blue-500 no-underline hover:underline bg-transparent border-0 cursor-pointer"
              onClick={handleBackToRegister}
            >
              {t("otp.backToRegister") || "← Back to Register"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OtpVerification;